import type { PixelCanvas } from '../pixel.js';
import { HH, HW, box, drawBox, drawBoxes, drawCylinder, faceQuad, faces, iso } from './iso.js';
import type { FurniDrawer } from './iso.js';

/**
 * Game tables (board games, cards, mahjong). Owned by the games feature.
 * Keys are furniture kinds from the matching protocol catalogue section; any
 * kind without a drawer here falls back to the placeholder crate.
 */

const TABLE_H = 26;
const WOOD = faces('#a0754c', '#c89b6d');
const LEG = faces('#5a3a22');

/** A flat quad lying on a horizontal surface at height h. */
function topQuad(pc: PixelCanvas, u0: number, u1: number, v0: number, v1: number, h: number, color: string): void {
  pc.poly([iso(u0, v0, h), iso(u1, v0, h), iso(u1, v1, h), iso(u0, v1, h)], color);
}

/** Four legs and a square top; returns the height of the playing surface. */
function squareTable(pc: PixelCanvas, half = 0.44, top = WOOD): number {
  const legs = [
    [-half + 0.02, -half + 0.02],
    [half - 0.1, -half + 0.02],
    [half - 0.1, half - 0.1],
    [-half + 0.02, half - 0.1],
  ].map(([u, v]) => box(u!, u! + 0.08, v!, v! + 0.08, 0, TABLE_H - 3, LEG));
  drawBoxes(pc, legs);
  drawBox(pc, box(-half, half, -half, half, TABLE_H - 3, TABLE_H + 1, top));
  return TABLE_H + 1;
}

/** An n x m grid of cells on a table top, coloured by `color(i, j)`. */
function cells(
  pc: PixelCanvas,
  h: number,
  u0: number,
  v0: number,
  size: number,
  nu: number,
  nv: number,
  color: (i: number, j: number) => string | null,
): void {
  for (let i = 0; i < nu; i += 1) {
    for (let j = 0; j < nv; j += 1) {
      const c = color(i, j);
      if (c) topQuad(pc, u0 + i * size, u0 + (i + 1) * size, v0 + j * size, v0 + (j + 1) * size, h, c);
    }
  }
}

function drawTicTacToe(pc: PixelCanvas): void {
  const h = squareTable(pc, 0.4, faces('#3d9e8c', '#e8d6b0'));
  const ink = '#2a1e19';
  const s = 0.6 / 3;
  for (const k of [1, 2]) {
    topQuad(pc, -0.3 + k * s - 0.02, -0.3 + k * s + 0.02, -0.3, 0.3, h, ink);
    topQuad(pc, -0.3, 0.3, -0.3 + k * s - 0.02, -0.3 + k * s + 0.02, h, ink);
  }
  // An X and an O mid-game.
  const x = iso(-0.2, -0.2, h);
  pc.line(x.x - 3, x.y - 2, x.x + 3, x.y + 2, '#d9433b');
  pc.line(x.x - 3, x.y + 2, x.x + 3, x.y - 2, '#d9433b');
  const o = iso(0.2, 0, h);
  pc.ellipse(o.x, o.y, 4, 2.2, '#3a6fd6');
  pc.ellipse(o.x, o.y, 2, 1, '#e8d6b0');
  const x2 = iso(0, 0.2, h);
  pc.line(x2.x - 3, x2.y - 2, x2.x + 3, x2.y + 2, '#d9433b');
  pc.line(x2.x - 3, x2.y + 2, x2.x + 3, x2.y - 2, '#d9433b');
}

function drawConnect4(pc: PixelCanvas, rotation: number): void {
  const h = squareTable(pc, 0.42, faces('#8a5a34', '#b17a48'));
  // The upright grid stands across the table, facing the viewer's left or right.
  const facingRight = rotation === 2 || rotation === 6;
  const blue = faces('#3a6fd6', '#5a8ae6');
  const frame = facingRight ? box(-0.04, 0.04, -0.4, 0.4, h, h + 30, blue) : box(-0.4, 0.4, -0.04, 0.04, h, h + 30, blue);
  // Feet.
  drawBox(pc, facingRight ? box(-0.1, 0.1, -0.42, -0.34, h, h + 3, blue) : box(-0.42, -0.34, -0.1, 0.1, h, h + 3, blue));
  drawBox(pc, facingRight ? box(-0.1, 0.1, 0.34, 0.42, h, h + 3, blue) : box(0.34, 0.42, -0.1, 0.1, h, h + 3, blue));
  drawBox(pc, frame);
  const face = facingRight ? 'right' : 'left';
  const discs = ['.', '.', '.', '.', '.', '.', '.', '.', '.', 'y', '.', '.', '.', '.', '.', 'r', 'y', '.', '.', '.', '.', 'y', 'r', 'r', '.', '.', 'y', '.', 'r', 'y', 'r', 'y', '.', 'r', 'r', 'y', 'r', 'y', 'r', 'y', 'r', 'y'];
  for (let row = 0; row < 6; row += 1) {
    for (let col = 0; col < 7; col += 1) {
      const d = discs[row * 7 + col]!;
      const color = d === 'r' ? '#d9433b' : d === 'y' ? '#f0bf36' : '#1f2b6b';
      const s0 = -0.35 + col * 0.1;
      const h0 = h + 26 - row * 4;
      faceQuad(pc, face, 0.04, s0 + 0.015, s0 + 0.085, h0 - 3, h0, color);
    }
  }
}

function drawCheckers(pc: PixelCanvas): void {
  const h = squareTable(pc);
  topQuad(pc, -0.42, 0.42, -0.42, 0.42, h, '#6b4226');
  const size = 0.1;
  cells(pc, h, -0.4, -0.4, size, 8, 8, (i, j) => ((i + j) % 2 === 0 ? '#e8d6b0' : '#2a2320'));
  // A few pieces.
  const piece = (i: number, j: number, color: string) => {
    const p = iso(-0.4 + (i + 0.5) * size, -0.4 + (j + 0.5) * size, h + 1);
    pc.ellipse(p.x, p.y, 2.6, 1.5, color);
  };
  for (const [i, j] of [
    [1, 0],
    [3, 0],
    [0, 1],
    [2, 1],
    [5, 2],
  ] as const) {
    piece(i, j, '#f4efe4');
  }
  for (const [i, j] of [
    [6, 7],
    [4, 7],
    [7, 6],
    [3, 4],
    [5, 6],
  ] as const) {
    piece(i, j, '#d9433b');
  }
}

function drawXiangqi(pc: PixelCanvas): void {
  const h = squareTable(pc, 0.44, faces('#8a5a34', '#b17a48'));
  topQuad(pc, -0.42, 0.42, -0.42, 0.42, h, '#e9c98a');
  const line = '#8a5a34';
  // 9 files x 10 ranks of lines, laid out along u (files) and v (ranks).
  const step = 0.08;
  const u0 = -0.32;
  const v0 = -0.36;
  for (let f = 0; f < 9; f += 1) {
    const u = u0 + f * step;
    topQuad(pc, u - 0.006, u + 0.006, v0, v0 + 9 * step, h, line);
  }
  for (let r = 0; r < 10; r += 1) {
    const v = v0 + r * step;
    topQuad(pc, u0, u0 + 8 * step, v - 0.006, v + 0.006, h, line);
  }
  // The river.
  topQuad(pc, u0 + 0.01, u0 + 8 * step - 0.01, v0 + 4 * step + 0.01, v0 + 5 * step - 0.01, h, '#7fb7d9');
  const piece = (f: number, r: number, color: string) => {
    const p = iso(u0 + f * step, v0 + r * step, h + 1);
    pc.ellipse(p.x, p.y, 3, 1.7, '#f4efe4');
    pc.px(p.x, p.y, color);
    pc.px(p.x - 1, p.y, color);
  };
  for (const [f, r] of [
    [0, 0],
    [4, 0],
    [8, 0],
    [1, 2],
    [2, 3],
  ] as const) {
    piece(f, r, '#2a1e19');
  }
  for (const [f, r] of [
    [0, 9],
    [4, 9],
    [8, 9],
    [7, 7],
    [4, 6],
  ] as const) {
    piece(f, r, '#d9433b');
  }
}

/** A flat mat: numbered squares, a green snake and a ladder. */
function drawSnakesMat(pc: PixelCanvas): void {
  const colors = ['#f0bf36', '#8fcf6a', '#7fb7d9', '#f19bb7', '#fffaf0'];
  topQuad(pc, -0.48, 0.48, -0.48, 0.48, 0, '#6b4226');
  cells(pc, 0, -0.45, -0.45, 0.09, 10, 10, (i, j) => colors[(i + j * 3) % colors.length]!);
  // Ladder: two rails and rungs.
  const a = iso(-0.3, 0.3, 0);
  const b = iso(0.2, -0.2, 0);
  pc.line(a.x - 3, a.y, b.x - 3, b.y, '#8a5a34');
  pc.line(a.x + 3, a.y, b.x + 3, b.y, '#8a5a34');
  for (let t = 0.1; t < 1; t += 0.18) {
    const x = a.x + (b.x - a.x) * t;
    const y = a.y + (b.y - a.y) * t;
    pc.line(x - 3, y, x + 3, y, '#8a5a34');
  }
  // Snake: a wavy green line with a head.
  let prev = iso(0.35, 0.35, 0);
  for (let t = 1; t <= 12; t += 1) {
    const u = 0.35 - t * 0.055;
    const v = 0.35 - t * 0.02 + Math.sin(t) * 0.05;
    const p = iso(u, v, 0);
    pc.line(prev.x, prev.y, p.x, p.y, '#3d9e4c');
    pc.line(prev.x, prev.y + 1, p.x, p.y + 1, '#2f7f3c');
    prev = p;
  }
  pc.ellipse(prev.x, prev.y, 3, 2, '#3d9e4c');
  pc.px(prev.x - 1, prev.y - 1, '#1d1716');
}

/** Round Texas Hold'em table: a pedestal, a padded wooden rail, green felt, chips and cards. */
function drawPokerTable(pc: PixelCanvas): void {
  const h = TABLE_H;
  // Pedestal and foot.
  drawCylinder(pc, 0.22, 0, 3, '#4a2f1b', '#3a2616');
  drawCylinder(pc, 0.07, 3, h - 4, '#6b4226', '#5a3a22');
  // Rail (a thick wooden rim) and the felt inside it.
  drawCylinder(pc, 0.47, h - 4, h, '#7a4a26', '#5a3a22');
  const c = iso(0, 0, h);
  const r = (k: number) => [k * HW * Math.SQRT2, k * HH * Math.SQRT2] as const;
  const [rx0, ry0] = r(0.47);
  pc.ellipse(c.x, c.y, rx0, ry0, '#8a5a34');
  const [rx1, ry1] = r(0.38);
  pc.ellipse(c.x, c.y, rx1, ry1, '#1d8573');
  const [rx2, ry2] = r(0.34);
  pc.ellipse(c.x, c.y + 0.5, rx2, ry2, '#2aa894');
  // Community cards across the middle.
  for (let k = -2; k <= 2; k += 1) {
    const p = iso(k * 0.085, -k * 0.085, h);
    pc.rect(p.x - 2, p.y - 3, 4, 5, '#3a2616');
    pc.rect(p.x - 1.5, p.y - 2.5, 3, 4, '#fffbee');
    pc.px(p.x - 1, p.y - 2, k % 2 === 0 ? '#c23e33' : '#3a2616');
  }
  // Chip stacks near two seats.
  const stack = (u: number, v: number, color: string, n: number) => {
    const p = iso(u, v, h);
    for (let i = 0; i < n; i += 1) {
      pc.ellipse(p.x, p.y - i * 1.5, 2.2, 1.1, i % 2 === 0 ? color : '#fffbee');
    }
  };
  stack(0.2, 0.12, '#e8574b', 4);
  stack(0.25, 0.02, '#2f6fd0', 3);
  stack(-0.14, 0.24, '#ffc83d', 5);
  // Dealer button.
  const d = iso(-0.22, -0.08, h);
  pc.ellipse(d.x, d.y, 1.8, 1, '#fffbee');
}

/** Square mahjong table: green baize, four walls of tiles and a few discards. */
function drawMahjongTable(pc: PixelCanvas): void {
  const h = squareTable(pc, 0.46, faces('#6b4226', '#8a5a34'));
  topQuad(pc, -0.4, 0.4, -0.4, 0.4, h, '#2aa894');
  topQuad(pc, -0.4, 0.4, -0.4, -0.37, h, '#1d8573');
  topQuad(pc, 0.37, 0.4, -0.4, 0.4, h, '#1d8573');
  // The four walls: rows of tiles, cream faces over their green backs.
  const tile = { top: '#fffbee', left: '#e9dcb8', right: '#1d8573' };
  const walls: ReturnType<typeof box>[] = [];
  for (let k = 0; k < 5; k += 1) {
    const s = -0.25 + k * 0.1;
    walls.push(box(s, s + 0.085, -0.34, -0.27, h, h + 4, tile));
    walls.push(box(0.27, 0.34, s, s + 0.085, h, h + 4, tile));
    walls.push(box(s, s + 0.085, 0.27, 0.34, h, h + 4, tile));
    walls.push(box(-0.34, -0.27, s, s + 0.085, h, h + 4, tile));
  }
  drawBoxes(pc, walls);
  // A few discards face up in the middle.
  const colors = ['#c23e33', '#2f6fd0', '#1d8573', '#3a2616'];
  for (const [i, [u, v]] of [
    [-0.1, -0.08],
    [0.04, -0.1],
    [-0.06, 0.06],
    [0.08, 0.04],
  ].entries()) {
    topQuad(pc, u!, u! + 0.08, v!, v! + 0.09, h + 1, '#fffbee');
    const p = iso(u! + 0.04, v! + 0.045, h + 1);
    pc.px(p.x, p.y, colors[i]!);
  }
}

/** Spice Bazaar: a square table with deep purple velvet, rows of spice tokens and a fan of cards. */
function drawBazaarTable(pc: PixelCanvas): void {
  const h = squareTable(pc, 0.46, faces('#5a3a22', '#7a4f2e'));
  topQuad(pc, -0.4, 0.4, -0.4, 0.4, h, '#4b2a6b');
  topQuad(pc, -0.4, 0.4, -0.4, -0.37, h, '#3a1f55');
  topQuad(pc, 0.37, 0.4, -0.4, 0.4, h, '#3a1f55');
  // Five stacks of spice tokens along the middle: white, blue, green, red, black.
  const spices = ['#f4efe4', '#3a6fd6', '#2ba64c', '#d9433b', '#2a1e19'];
  spices.forEach((color, i) => {
    const p = iso(-0.24 + i * 0.12, 0.04 - i * 0.12 + 0.24, h);
    for (let k = 0; k < 3; k += 1) pc.ellipse(p.x, p.y - k * 1.5, 2.4, 1.2, k === 2 ? color : '#00000040');
    pc.ellipse(p.x, p.y - 3, 2.2, 1.1, color);
    pc.px(p.x - 1, p.y - 3.5, '#ffffff');
  });
  // Three rows of stall cards, and a pair of patron tiles.
  const deck = (u: number, v: number, back: string) => {
    topQuad(pc, u, u + 0.09, v, v + 0.12, h + 1, back);
    topQuad(pc, u + 0.01, u + 0.08, v + 0.01, v + 0.11, h + 1, '#fffbee');
  };
  deck(-0.32, -0.32, '#c9a227');
  deck(-0.18, -0.32, '#3a6fd6');
  deck(-0.04, -0.32, '#2ba64c');
  deck(0.1, -0.32, '#d9433b');
  topQuad(pc, 0.22, 0.31, -0.32, -0.21, h + 1, '#c9a227');
}

/** Shadow Court's round table: dark oak, a royal-blue top with a gold ring, and a sceptre laid across it. */
function drawCourtTable(pc: PixelCanvas): void {
  const h = TABLE_H;
  drawCylinder(pc, 0.24, 0, 3, '#3a2616', '#2a1a0e');
  drawCylinder(pc, 0.08, 3, h - 4, '#5a3a22', '#4a2f1b');
  drawCylinder(pc, 0.48, h - 4, h, '#6b4226', '#4a2f1b');
  const c = iso(0, 0, h);
  const r = (k: number) => [k * HW * Math.SQRT2, k * HH * Math.SQRT2] as const;
  const [rx0, ry0] = r(0.48);
  pc.ellipse(c.x, c.y, rx0, ry0, '#c9a227');
  const [rx1, ry1] = r(0.4);
  pc.ellipse(c.x, c.y, rx1, ry1, '#1f3f8a');
  const [rx2, ry2] = r(0.33);
  pc.ellipse(c.x, c.y + 0.5, rx2, ry2, '#2c56b8');
  // Twelve seats round the ring.
  for (let k = 0; k < 10; k += 1) {
    const a = (k / 10) * Math.PI * 2;
    pc.px(Math.round(c.x + Math.cos(a) * rx1 * 0.82), Math.round(c.y + Math.sin(a) * ry1 * 0.82), '#ffe27a');
  }
  // The sword: blade, cross-guard and pommel.
  const a = iso(-0.16, -0.16, h);
  const b = iso(0.16, 0.16, h);
  pc.line(a.x, a.y, b.x, b.y, '#e8eef5');
  pc.line(a.x, a.y - 1, b.x, b.y - 1, '#ffffff');
  const g = iso(-0.1, -0.1, h);
  pc.rect(g.x - 1, g.y - 3, 2, 5, '#c9a227');
  pc.ellipse(a.x - 1, a.y, 1.5, 1, '#ffe27a');
}

/** Round four-seat card table: deep green baize, a fan of face-up cards and a trick in the middle. */
function drawFloatingBridgeTable(pc: PixelCanvas): void {
  const h = TABLE_H;
  drawCylinder(pc, 0.22, 0, 3, '#3a2616', '#2a1a0e');
  drawCylinder(pc, 0.07, 3, h - 4, '#5a3a22', '#4a2f1b');
  drawCylinder(pc, 0.46, h - 4, h, '#6b4226', '#4a2f1b');
  const c = iso(0, 0, h);
  const r = (k: number) => [k * HW * Math.SQRT2, k * HH * Math.SQRT2] as const;
  const [rx0, ry0] = r(0.46);
  pc.ellipse(c.x, c.y, rx0, ry0, '#8a5a34');
  const [rx1, ry1] = r(0.38);
  pc.ellipse(c.x, c.y, rx1, ry1, '#1d8573');
  const [rx2, ry2] = r(0.34);
  pc.ellipse(c.x, c.y + 0.5, rx2, ry2, '#2aa894');
  // A trick in progress: four cards fanned toward the middle, one per seat.
  const trick: readonly [number, number, string][] = [
    [0, -0.14, '#c23e33'],
    [0.14, 0, '#3a2616'],
    [0, 0.14, '#3a2616'],
    [-0.14, 0, '#c23e33'],
  ];
  for (const [u, v, pip] of trick) {
    const p = iso(u, v, h);
    pc.rect(p.x - 2.5, p.y - 3.5, 5, 7, '#3a2616');
    pc.rect(p.x - 2, p.y - 3, 4, 6, '#fffbee');
    pc.px(p.x - 1, p.y - 2, pip);
    pc.px(p.x, p.y - 2, pip);
  }
  // Four small card-back fans near the rail, one per seat.
  const fan = (u: number, v: number) => {
    const p = iso(u, v, h);
    for (let i = 0; i < 3; i += 1) pc.rect(p.x - 3 + i * 1.5, p.y - 2, 3, 5, i === 1 ? '#d9433b' : '#a8362f');
  };
  fan(0, -0.3);
  fan(0.3, 0);
  fan(0, 0.3);
  fan(-0.3, 0);
}

export const GAMES_DRAWERS: Record<string, FurniDrawer> = {
  tictactoe_table: drawTicTacToe,
  connect4_table: drawConnect4,
  checkers_table: drawCheckers,
  xiangqi_table: drawXiangqi,
  snakes_mat: drawSnakesMat,
  poker_table: drawPokerTable,
  mahjong_table: drawMahjongTable,
  court_table: drawCourtTable,
  bazaar_table: drawBazaarTable,
  floatingbridge_table: drawFloatingBridgeTable,
};
